import React, { useState } from "react";
import {
  Card,
  Typography,
  Tag,
  Divider,
  Button,
  Input,
  Modal,
  message,
  Alert,
} from "antd";
import {
  DollarOutlined,
  HistoryOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ShoppingCartOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import ExpirationCountdown from "./ExpirationCountdown";
import {
  startNegotiation,
  requestAmountChange,
} from "../services/quoteNegotiationService.js";
import { manuallyExpireQuote } from "../services/quoteExpirationService.js";
import OrderConfirmationViewModal from "./OrderConfirmationViewModal";

const { Text, Title } = Typography;

const StatusContainer = styled.div`
  margin-bottom: 1rem;
`;

const AmountContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const NegotiationHistory = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 0.375rem;
  border-left: 3px solid #1890ff;
`;

const QuoteStatusManager = ({
  quote,
  onUpdate,
  userRole,
  userId,
  canManage,
}) => {
  const navigate = useNavigate();
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [newAmount, setNewAmount] = useState("");
  const [negotiationModal, setNegotiationModal] = useState(false);
  const [negotiationReason, setNegotiationReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [expirationModal, setExpirationModal] = useState(false);
  const [expirationReason, setExpirationReason] = useState("");
  const [orderConfirmationModalVisible, setOrderConfirmationModalVisible] =
    useState(false);

  const statusOptions = [
    { value: "pending", label: "⏳ Pending", color: "orange" },
    { value: "sent", label: "📤 Sent", color: "blue" },
    { value: "negotiating", label: "🤝 Negotiating", color: "cyan" },
    { value: "approved", label: "✅ Approved", color: "green" },
    { value: "accepted", label: "🎯 Accepted", color: "lime" },
    { value: "pending_payment", label: "💳 Pending Payment", color: "orange" },
    { value: "deposit_verified", label: "✅ Deposit Verified", color: "green" },
    { value: "ordered", label: "📦 Order Created", color: "purple" },
    { value: "rejected", label: "❌ Rejected", color: "red" },
    { value: "expired", label: "⏰ Expired", color: "default" },
  ];

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find((s) => s.value === status);
    return statusOption?.color || "default";
  };

  const getStatusLabel = (status) => {
    const statusOption = statusOptions.find((s) => s.value === status);
    return statusOption?.label || status;
  };

  const canEditStatus = canManage || userRole === "admin";
  const canNegotiate = canManage && ["sent", "pending"].includes(quote.status);
  const canExpire =
    canManage && !["expired", "accepted", "ordered"].includes(quote.status);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(amount);
  };

  const handleStartNegotiation = async () => {
    if (!negotiationReason.trim()) {
      message.error("Please provide a reason for negotiation");
      return;
    }

    if (!userId) {
      message.error("User ID not available. Please refresh and try again.");
      return;
    }

    setLoading(true);
    try {
      const result = await startNegotiation({
        quoteId: quote.id,
        initiatedBy: userId || "unknown-user",
        reason: negotiationReason,
      });

      if (result.success) {
        message.success("Negotiation started successfully");
        setNegotiationModal(false);
        setNegotiationReason("");
        if (onUpdate) onUpdate();
      } else {
        message.error(result.error || "Failed to start negotiation");
      }
    } catch (error) {
      message.error("Failed to start negotiation");
      console.error("Negotiation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAmountChange = async () => {
    if (!newAmount || isNaN(newAmount) || newAmount <= 0) {
      message.error("Please enter a valid amount");
      return;
    }

    if (!userId) {
      message.error("User ID not available. Please refresh and try again.");
      return;
    }

    setLoading(true);
    try {
      const result = await requestAmountChange({
        quoteId: quote.id,
        newAmount: parseFloat(newAmount),
        requestedBy: userId || "unknown-user",
        reason: "Amount adjustment requested",
      });

      if (result.success) {
        message.success(result.message);
        setIsEditingAmount(false);
        setNewAmount("");
        if (onUpdate) onUpdate();
      } else {
        message.error(result.error || "Failed to change amount");
      }
    } catch (error) {
      message.error("Failed to change amount");
      console.error("Amount change error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleManualExpiration = async () => {
    if (!expirationReason.trim()) {
      message.error("Please provide a reason for expiration");
      return;
    }

    if (!userId) {
      message.error("User ID not available. Please refresh and try again.");
      return;
    }

    setLoading(true);
    try {
      const result = await manuallyExpireQuote(quote.id, {
        reason: expirationReason.trim(),
        expiredBy: userId || "unknown-user",
      });

      if (result.success) {
        message.success(
          "Quote expired successfully. WhatsApp notification sent to customer."
        );
        setExpirationModal(false);
        setExpirationReason("");
        if (onUpdate) onUpdate();
      } else {
        message.error(result.error || "Failed to expire quote");
      }
    } catch (error) {
      console.error("Error expiring quote:", error);
      message.error("Failed to expire quote");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Quote Management" size="small">
      <StatusContainer>
        <Text strong>Current Status: </Text>
        <Tag color={getStatusColor(quote.status)}>
          {getStatusLabel(quote.status)}
        </Tag>

        {/* Show order link when status is ordered */}
        {quote.status === "ordered" && (
          <>
            {(quote.orderId || quote.payment?.orderId) && (
              <Alert
                message="Order Created"
                description={
                  <div>
                    <Text>
                      This quote has been converted to an order.{" "}
                      <Button
                        type="link"
                        size="small"
                        icon={<ShoppingCartOutlined />}
                        onClick={() => navigate(`/orders/${quote.orderId || quote.payment?.orderId}`)}
                        style={{ padding: 0, height: "auto" }}
                      >
                        View Order {quote.orderId || quote.payment?.orderId}
                      </Button>
                    </Text>
                  </div>
                }
                type="success"
                showIcon
                style={{ marginTop: "0.75rem", marginBottom: "1rem" }}
              />
            )}
            <div style={{ marginBottom: "1rem" }}>
              <Button
                type="primary"
                icon={<MailOutlined />}
                onClick={() => setOrderConfirmationModalVisible(true)}
                block
              >
                View Confirmation Email
              </Button>
            </div>
          </>
        )}

        {/* Expiration Countdown */}
        <ExpirationCountdown quote={quote} />
      </StatusContainer>

      <AmountContainer>
        <Text strong>Amount: </Text>
        {isEditingAmount ? (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Input
              type="number"
              value={newAmount}
              onChange={(e) => setNewAmount(e.target.value)}
              placeholder="Enter new amount"
              style={{ width: "120px" }}
            />
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              onClick={handleAmountChange}
              loading={loading}
            >
              Save
            </Button>
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={() => {
                setIsEditingAmount(false);
                setNewAmount("");
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexWrap: "wrap",
            }}
          >
            {/* Calculate total cost: totalCost if available, otherwise estimatedCost + addOnsCost */}
            {(() => {
              const totalCost =
                quote.totalCost !== undefined
                  ? quote.totalCost
                  : (quote.estimatedCost || 0) + (quote.addOnsCost || 0);
              const baseCost = quote.estimatedCost || 0;
              const addOns = quote.addOnsCost || 0;

              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.25rem",
                  }}
                >
                  <span style={{ fontSize: "1.125rem", fontWeight: "600" }}>
                    {totalCost > 0
                      ? formatCurrency(totalCost)
                      : "Not specified"}
                  </span>
                  {addOns > 0 && (
                    <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                      Base: {formatCurrency(baseCost)} + Add-ons:{" "}
                      {formatCurrency(addOns)}
                    </Text>
                  )}
                </div>
              );
            })()}
            {canManage && quote.status === "negotiating" && (
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  setNewAmount(quote.estimatedCost?.toString() || "");
                  setIsEditingAmount(true);
                }}
              >
                Edit
              </Button>
            )}
          </div>
        )}
      </AmountContainer>

      {/* Negotiation Actions */}
      {canNegotiate && (
        <div style={{ marginBottom: "1rem" }}>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setNegotiationModal(true);
            }}
            loading={loading}
            block
          >
            Start Negotiation
          </Button>
        </div>
      )}

      {/* Deposit verification lives on the Verification tab (SOP #3) */}
      {(quote.payment?.depositVerified ||
        quote.status === "deposit_verified") && (
        <Alert
          message="Deposit Verified"
          description={
            <Text>
              Deposit has been verified. Order ID:{" "}
              {quote.payment?.orderId ? (
                <Button
                  type="link"
                  size="small"
                  icon={<ShoppingCartOutlined />}
                  onClick={() => navigate(`/orders/${quote.payment.orderId}`)}
                  style={{ padding: 0, height: "auto" }}
                >
                  {quote.payment.orderId}
                </Button>
              ) : (
                "N/A"
              )}
              . Use the Verification tab for receipt and templates.
            </Text>
          }
          type="success"
          showIcon
          style={{ marginBottom: "1rem" }}
        />
      )}

      {/* Expiration Actions */}
      {canExpire && (
        <div style={{ marginBottom: "1rem" }}>
          <Button
            type="default"
            danger
            icon={<ExclamationCircleOutlined />}
            onClick={() => {
              setExpirationModal(true);
            }}
            loading={loading}
            block
          >
            Expire Quote
          </Button>
        </div>
      )}

      {/* Debug info */}
      {!canNegotiate && (
        <div
          style={{
            marginBottom: "1rem",
            padding: "8px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
          }}
        >
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Negotiation not available: canManage={canManage.toString()}, status=
            {quote.status}
          </Text>
        </div>
      )}

      {quote.negotiationHistory && quote.negotiationHistory.length > 0 && (
        <NegotiationHistory>
          <Title level={5}>📝 Negotiation History</Title>
          {quote.negotiationHistory.slice(-3).map((entry, index) => (
            <div
              key={index}
              style={{ marginBottom: "0.5rem", fontSize: "0.875rem" }}
            >
              <Text strong>{entry.action}</Text>
              <br />
              <Text type="secondary">
                {new Date(entry.timestamp).toLocaleString()} by {entry.user}
                {entry.notes && ` - ${entry.notes}`}
              </Text>
            </div>
          ))}
        </NegotiationHistory>
      )}

      <Divider style={{ margin: "12px 0" }} />

      {/* Negotiation Modal */}
      <Modal
        title="Start Negotiation"
        open={negotiationModal}
        onOk={handleStartNegotiation}
        onCancel={() => {
          setNegotiationModal(false);
          setNegotiationReason("");
        }}
        confirmLoading={loading}
        okText="Start Negotiation"
        cancelText="Cancel"
        zIndex={99999}
        style={{ zIndex: 99999 }}
        maskStyle={{ zIndex: 99998 }}
        className="negotiation-modal-override"
      >
        <div style={{ marginBottom: "1rem" }}>
          <Text strong>Quote ID:</Text> {quote.id}
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <Text strong>Current Status:</Text> {getStatusLabel(quote.status)}
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <Text strong>Reason for Negotiation:</Text>
          <Input.TextArea
            value={negotiationReason}
            onChange={(e) => setNegotiationReason(e.target.value)}
            placeholder="Explain why negotiation is needed..."
            rows={3}
            style={{ marginTop: "0.5rem" }}
          />
        </div>
      </Modal>

      {/* Expiration Modal */}
      <Modal
        title="Expire Quote"
        open={expirationModal}
        onOk={handleManualExpiration}
        onCancel={() => {
          setExpirationModal(false);
          setExpirationReason("");
        }}
        confirmLoading={loading}
        okText="Expire Quote"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        zIndex={99999}
        style={{ zIndex: 99999 }}
        maskStyle={{ zIndex: 99998 }}
        className="negotiation-modal-override"
      >
        <div style={{ marginBottom: "1rem" }}>
          <Text strong>Quote ID:</Text> {quote.id}
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <Text strong>Current Status:</Text> {getStatusLabel(quote.status)}
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <Text strong>Reason for Expiration:</Text>
          <Input.TextArea
            value={expirationReason}
            onChange={(e) => setExpirationReason(e.target.value)}
            placeholder="Explain why this quote should be expired..."
            rows={3}
            style={{ marginTop: "0.5rem" }}
          />
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <Text type="warning">
            ⚠️ This action cannot be undone. The quote will be marked as expired
            and will no longer be available for acceptance.
          </Text>
        </div>
      </Modal>

      {/* Order Confirmation View Modal - View confirmation templates for ordered quotes */}
      {quote.status === "ordered" && (
        <OrderConfirmationViewModal
          visible={orderConfirmationModalVisible}
          quote={quote}
          orderId={quote.orderId || quote.payment?.orderId}
          onClose={() => setOrderConfirmationModalVisible(false)}
        />
      )}
    </Card>
  );
};

export default QuoteStatusManager;
